import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, AuthState, UserRole } from '@/shared/types';
import { storage } from '@/shared/lib/utils';
import { normalizeStoredUser } from '@/shared/lib/authUser';
import {
  STORAGE_KEYS,
  hasPermissionInList,
  hasAnyPermissionInList,
  hasAllPermissionsInList,
  type PermissionName,
} from '@/shared/constants';

const getInitialState = (): AuthState => {
  const token = storage.get<string>(STORAGE_KEYS.TOKEN);
  const rawUser = storage.get<unknown>(STORAGE_KEYS.USER);
  const user = rawUser ? normalizeStoredUser(rawUser) : null;

  return {
    user,
    token: token,
    isAuthenticated: !!token && !!user,
    isLoading: false,
  };
};

const initialState: AuthState = getInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isLoading = false;

      // Persist to storage
      storage.set(STORAGE_KEYS.TOKEN, action.payload.token);
      storage.set(STORAGE_KEYS.USER, action.payload.user);
    },

    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        storage.set(STORAGE_KEYS.USER, state.user);
      }
    },

    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;

      // Clear storage
      storage.remove(STORAGE_KEYS.TOKEN);
      storage.remove(STORAGE_KEYS.USER);
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { setCredentials, updateUser, logout, setLoading } =
  authSlice.actions;

export const authReducer = authSlice.reducer;

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectUserRole = (state: { auth: AuthState }) =>
  state.auth.user?.role;
export const selectUserRoleName = (state: { auth: AuthState }) =>
  state.auth.user?.roleName ?? '';
export const selectUserRoleId = (state: { auth: AuthState }) =>
  state.auth.user?.roleId ?? null;
export const selectUserPermissions = (state: { auth: AuthState }) =>
  state.auth.user?.permissions ?? [];

export const selectHasPermission =
  (permission: PermissionName) => (state: { auth: AuthState }) => {
    const permissions = state.auth.user?.permissions ?? [];
    return hasPermissionInList(permissions, permission);
  };

export const selectHasAnyPermission =
  (permissions: PermissionName[]) => (state: { auth: AuthState }) => {
    const userPermissions = state.auth.user?.permissions ?? [];
    return hasAnyPermissionInList(userPermissions, permissions);
  };

export const selectHasAllPermissions =
  (permissions: PermissionName[]) => (state: { auth: AuthState }) => {
    const userPermissions = state.auth.user?.permissions ?? [];
    return hasAllPermissionsInList(userPermissions, permissions);
  };

export const selectHasRole =
  (roles: UserRole | UserRole[]) => (state: { auth: AuthState }) => {
    const user = state.auth.user;
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };
